/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { Dispatch } from '@reduxjs/toolkit';

import { SecurityHelper } from 'sarsaparilla';
import type { CsrAccount, RecAccount } from 'sarsaparilla/auth';

import { csrSessionWritten, loggedOut, sessionWritten } from './auth.slice';
import type { AuthSession } from './auth.slice';

/**
 * Write the visitor/customer session to localStorage (`recaccount`) and mirror
 * it into Redux. localStorage remains the source of truth; Redux is the
 * reactive projection.
 */
export function writeSession(dispatch: Dispatch, profile: AuthSession): void {
    window.localStorage.setItem('recaccount', JSON.stringify(profile));
    dispatch(sessionWritten(profile));
}

function isAuthSession(value: RecAccount | object): value is AuthSession {
    return 'access_token' in value && 'expiration' in value;
}

function isCsrAccount(value: CsrAccount | object): value is CsrAccount {
    return 'access_token' in value && 'expiration' in value;
}

/**
 * Restore Redux from the stored session. Reads still go through sarsaparilla
 * security helpers (token refresh lives there until a later phase).
 */
export function hydrateFromStorage(dispatch: Dispatch): void {
    if (SecurityHelper.isLoggedIn()) {
        const recAccount = SecurityHelper.getRecAccount();
        if (isAuthSession(recAccount)) {
            dispatch(sessionWritten(recAccount));
        }
    } else if (SecurityHelper.isGuest()) {
        const guestAccount = SecurityHelper.getGuestAccount();
        if (isAuthSession(guestAccount)) {
            dispatch(sessionWritten(guestAccount));
        }
    }

    if (SecurityHelper.isExternalCSRLoggedIn()) {
        const csrAccount = SecurityHelper.getExternalCSRAccount();
        if (isCsrAccount(csrAccount)) {
            dispatch(csrSessionWritten(csrAccount));
        }
    }
}

/**
 * Nuclear sign-out: wipe all web storage, clear the Redux session (visitor and
 * CSR), and hard-redirect to the widget home URL or `/`.
 */
export function logout(dispatch: Dispatch): void {
    window.localStorage.clear();
    window.sessionStorage.clear();
    dispatch(loggedOut());
    window.location.assign(SecurityHelper.getWidgetHomeUrl() ?? '/');
}

// cSpell:ignore recaccount
